import { getIcon, SLRSpinner, SLRTable, TableColumn } from '@slr/shared-library';
import {
  ADMINISTRATION_OFFERS_PATH,
  ADMINISTRATION_USERS_PATH
} from 'configs';
import { useSearchPlatformAdminOrganizations } from 'hooks/admin/useSearchPlatformAdminOrganizations';
import { Empty } from 'pages/organizations';
import { FC, FormEvent, useMemo } from 'react';
import {
  Button,
  Col,
  Form,
  InputGroup,
  Pagination,
  Row
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';
import { openAdministrationViewWithSearch } from './administrationNavigation';
import { useAdministrationSearchUrlSync } from './useAdministrationSearchUrlSync';
import { PlatformAdminOrganization } from 'services/admin/platformAdminOrganization.types';
import { AdministrationOutletContext } from './AdministrationPage';
import './AdministrationOrganizationsPage.scss';

const PAGE_SIZE_OPTIONS = [1, 10, 25, 50] as const;

const formatMemberName = (member: PlatformAdminOrganization['members'][number]) => {
  const fullName = [member.firstName, member.lastName].filter(Boolean).join(' ');
  return fullName || member.emailAddress || '';
};

export const AdministrationOrganizationsPage: FC = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'administrationOrganizationsPage'
  });
  const navigate = useNavigate();
  const {
    organizationsFilters,
    setOrganizationsFilters,
    setUsersFilters,
    setOffersFilters
  } = useOutletContext<AdministrationOutletContext>();
  const { searchInput, appliedSearch, page, pageSize } = organizationsFilters;
  const { commitSearchToUrl } =
    useAdministrationSearchUrlSync(setOrganizationsFilters);

  const { data, isPending } = useSearchPlatformAdminOrganizations(
    appliedSearch,
    page,
    pageSize
  );

  const rows = useMemo(() => data?.content ?? [], [data]);

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmedSearch = searchInput.trim();
    setOrganizationsFilters((current) => ({
      ...current,
      page: 0,
      appliedSearch: trimmedSearch
    }));
    commitSearchToUrl(trimmedSearch);
  };

  const handleOpenMemberUser = (emailAddress: string) => {
    openAdministrationViewWithSearch(
      navigate,
      ADMINISTRATION_USERS_PATH,
      emailAddress,
      setUsersFilters,
      { roleFilter: 'all' }
    );
  };

  const handleOpenOrganizationOffers = (organizationName: string) => {
    openAdministrationViewWithSearch(
      navigate,
      ADMINISTRATION_OFFERS_PATH,
      organizationName,
      setOffersFilters
    );
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setOrganizationsFilters((current) => ({
      ...current,
      page: 0,
      pageSize: nextPageSize
    }));
  };

  const columns: TableColumn<PlatformAdminOrganization>[] = useMemo(
    () => [
      {
        label: t('columns.name'),
        accessor: 'name'
      },
      {
        label: t('columns.email'),
        accessor: 'email',
        cellFn: ({ data: organization }) => organization.email || t('emptyValue')
      },
      {
        label: t('columns.address'),
        accessor: 'address',
        cellFn: ({ data: organization }) =>
          organization.address || t('emptyValue')
      },
      {
        label: t('columns.members'),
        accessor: 'members',
        cellFn: ({ data: organization }) =>
          organization.members.length === 0 ? (
            t('emptyValue')
          ) : (
            <div className="administration-organizations-page__members">
              {organization.members.map((member, index) => {
                const memberLabel = formatMemberName(member);
                const memberEmail = member.emailAddress?.trim();
                return (
                  <span key={`${memberEmail ?? 'member'}-${index}`}>
                    {memberEmail ? (
                      <Button
                        type="button"
                        variant="link"
                        className="administration-cross-link"
                        onClick={() => handleOpenMemberUser(memberEmail)}
                        aria-label={t('openMemberUser', { email: memberEmail })}
                        title={memberEmail}
                      >
                        {memberLabel}
                        {` (${memberEmail})`}
                      </Button>
                    ) : (
                      memberLabel
                    )}
                  </span>
                );
              })}
            </div>
          )
      },
      {
        label: t('columns.offers'),
        accessor: 'offerCount',
        cellFn: ({ data: organization }) => {
          const offerCount = organization.offerCount ?? 0;
          if (offerCount <= 0) {
            return offerCount;
          }
          return (
            <Button
              type="button"
              variant="link"
              className="administration-cross-link"
              onClick={() => handleOpenOrganizationOffers(organization.name)}
              aria-label={t('openOrganizationOffers', {
                name: organization.name,
                count: offerCount
              })}
              title={organization.name}
            >
              {offerCount}
            </Button>
          );
        }
      }
    ],
    [t]
  );

  const totalPages = Math.max(data?.totalPages ?? 0, 1);

  return (
    <div className="administration-organizations-page">
      <Row className="administration-toolbar align-items-center justify-content-center g-3 mb-4">
        <Col xs={12} sm={8} md={6} lg={4} className="administration-toolbar__search">
          <Form onSubmit={handleSearchSubmit}>
            <InputGroup>
              <Form.Control
                type="search"
                value={searchInput}
                onChange={(event) => {
                  const nextSearchInput = event.target.value;
                  setOrganizationsFilters((current) => {
                    if (
                      nextSearchInput === '' &&
                      current.appliedSearch !== ''
                    ) {
                      commitSearchToUrl('');
                      return {
                        ...current,
                        searchInput: nextSearchInput,
                        appliedSearch: '',
                        page: 0
                      };
                    }
                    return {
                      ...current,
                      searchInput: nextSearchInput
                    };
                  });
                }}
                placeholder={t('searchPlaceholder')}
                aria-label={t('searchPlaceholder')}
              />
              <Button type="submit" variant="primary">
                {t('search')}
              </Button>
            </InputGroup>
          </Form>
        </Col>
        <Col xs="auto">
          <Form.Group
            controlId="administration-organizations-page-size"
            className="administration-organizations-page__page-size d-flex align-items-center gap-2"
          >
            <Form.Label className="mb-0 text-nowrap">
              {t('pageSize')}
            </Form.Label>
            <Form.Select
              value={pageSize}
              onChange={(event) =>
                handlePageSizeChange(Number(event.target.value))
              }
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {isPending ? (
        <SLRSpinner />
      ) : (
        <>
          <SLRTable<PlatformAdminOrganization>
            emptyView={
              <Empty
                labels={
                  t('empty', { returnObjects: true }) as {
                    top: string;
                    bottom: string;
                  }
                }
              />
            }
            sortIcon={getIcon('fas', 'long-arrow-up')}
            columns={columns}
            data={rows}
          />

          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.Prev
                disabled={page <= 0}
                onClick={() =>
                  setOrganizationsFilters((current) => ({
                    ...current,
                    page: Math.max(0, current.page - 1)
                  }))
                }
              />
              {Array.from({ length: totalPages }, (_, index) => (
                <Pagination.Item
                  key={index}
                  active={index === page}
                  onClick={() =>
                    setOrganizationsFilters((current) => ({
                      ...current,
                      page: index
                    }))
                  }
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page >= totalPages - 1}
                onClick={() =>
                  setOrganizationsFilters((current) => ({
                    ...current,
                    page: Math.min(totalPages - 1, current.page + 1)
                  }))
                }
              />
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
};
